import { FileSpreadsheet } from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";


import { SeloBeta, AvisoBeta } from "@/components/BadgeBeta";

import { carregarDre } from "./api/dreApi";
import Avisos from "./components/Avisos";
import CadaCem from "./components/CadaCem";
import CardsResumo from "./components/CardsResumo";
import FiltroPeriodo from "./components/FiltroPeriodo";
import TabelaDre from "./components/TabelaDre";
import { intervalo, rotuloPeriodo } from "./lib/periodos";

/**
 * FINANCEIRO → DRE (Demonstrativo de Resultado do Exercício). Até a v1.5.783 o
 * menu chamava "Extrato Financeiro"; a rota continua /financeiro/extrato.
 *
 * A estrutura contábil e o cálculo vivem no backend (api/v1/dre); esta tela só
 * apresenta — assim o relatório é o mesmo para quem consultar por API ou aqui.
 */
export default function DrePage() {
  const [preset, setPreset] = useState("mes");
  const inicial = useMemo(() => intervalo("mes"), []);
  const [inicio, setInicio] = useState(inicial.inicio);
  const [fim, setFim] = useState(inicial.fim);
  const [regime, setRegime] = useState("competencia");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  // Preset troca as datas; "custom" respeita o que o lojista digitou.
  useEffect(() => {
    if (preset === "custom") return;
    const i = intervalo(preset);
    setInicio(i.inicio);
    setFim(i.fim);
  }, [preset]);

  const buscar = useCallback(async () => {
    if (!inicio || !fim || fim < inicio) return;
    setLoading(true);
    try {
      setDados(await carregarDre({ inicio, fim, regime }));
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Não consegui montar o DRE.");
    } finally {
      setLoading(false);
    }
  }, [inicio, fim, regime]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-5xl mx-auto">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <FileSpreadsheet className="w-6 h-6 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-bold">DRE</h1>
          <SeloBeta />
        </div>
        <p className="text-muted-foreground">
          Demonstrativo de Resultado — quanto entrou, quanto saiu e o que sobrou
          em {rotuloPeriodo(inicio, fim)}.
        </p>
      </div>

      <AvisoBeta>
        O DRE está em <strong className="text-foreground">beta</strong>. Os números
        saem das vendas concluídas, de cada despesa lançada em Financeiro › Despesas
        e das taxas configuradas em Financeiro › Formas de Pagamento — quanto mais
        completo o lançamento, mais fiel o resultado. Clique numa linha para ver o
        que formou o número. Confira com seu contador antes de usar para declaração.
      </AvisoBeta>

      <FiltroPeriodo
        preset={preset} setPreset={setPreset}
        inicio={inicio} setInicio={setInicio}
        fim={fim} setFim={setFim}
        regime={regime} setRegime={setRegime}
      />

      <CardsResumo resumo={dados?.resumo} loading={loading} />

      {!loading && <CadaCem resumo={dados?.resumo} />}

      {!loading && <Avisos avisos={dados?.avisos} />}

      <TabelaDre linhas={dados?.linhas} regime={regime} loading={loading} />

      {!loading && dados?.cancelados?.quantidade > 0 && (
        <p className="text-xs text-muted-foreground px-1">
          Fora do resultado: {dados.cancelados.quantidade} pedido(s) cancelado(s)
          no período. Cancelamento nunca foi receita, então não entra como
          dedução — mas é o que você deixou de vender.
        </p>
      )}
    </div>
  );
}
